import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Clock, Play, FileText, Pill, AlertTriangle, CheckCircle,
  RefreshCw, Wifi, WifiOff, ChevronRight, Bell, Calendar,
  ClipboardList, Upload, Camera, Truck, ShieldAlert, Square, Activity,
} from 'lucide-react';
import { getQueueStats } from '@/lib/offline/actionQueue';
import { syncAllActions, isOnline } from '@/lib/offline/syncEngine';
import { initSyncService } from '@/lib/offline/syncService';
import { useMobileDashboard, useNotificationCounts, useCacheInvalidation } from '@/hooks/useQueryCache';
import { MobileBottomNav } from '@/components/mobile/MobileNavigation';
import { useBranding } from '@/lib/branding';
import { useAuth } from '@/lib/auth';
import { registerDevice } from '@/lib/mobile/device';

const MIN_TOUCH_SIZE = 48;

initSyncService();

// Live countdown to a local-naive ISO timestamp (e.g. "2026-07-01T22:00:00")
function useCountdown(targetIso) {
  const [label, setLabel] = useState(null);

  useEffect(() => {
    if (!targetIso) { setLabel(null); return; }
    const compute = () => {
      const target = new Date(targetIso).getTime();
      if (isNaN(target)) { setLabel(null); return; }
      const diffMs = target - Date.now();
      if (diffMs <= 0) { setLabel('Now'); return; }
      const mins = Math.floor(diffMs / 60000);
      const days = Math.floor(mins / 1440);
      const hours = Math.floor((mins % 1440) / 60);
      const rem = mins % 60;
      if (days > 0) setLabel(`in ${days}d ${hours}h`);
      else if (hours > 0) setLabel(`in ${hours}h ${rem}m`);
      else setLabel(`in ${rem}m`);
    };
    compute();
    const t = setInterval(compute, 30000);
    return () => clearInterval(t);
  }, [targetIso]);

  return label;
}

function ConnectionStatus({ online }) {
  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
        online ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'
      }`}
      data-testid="mobile-connection-status"
    >
      {online ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
      {online ? 'Online' : 'Offline'}
    </div>
  );
}

function DashboardCard({ title, icon: Icon, children, onClick, badge, variant = 'default' }) {
  const variants = {
    default: 'bg-card hover:bg-muted/50',
    warning: 'bg-amber-500/5 border-amber-500/20',
    danger: 'bg-red-500/5 border-red-500/20',
    success: 'bg-green-500/5 border-green-500/20',
    primary: 'bg-primary/5 border-primary/20',
  };

  return (
    <Card
      className={`${variants[variant]} transition-all cursor-pointer active:scale-[0.98]`}
      onClick={onClick}
      data-testid={`mobile-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
              variant === 'danger' ? 'bg-red-500/10' :
              variant === 'warning' ? 'bg-amber-500/10' :
              variant === 'success' ? 'bg-green-500/10' :
              'bg-primary/10'
            }`}>
              <Icon className={`h-5 w-5 ${
                variant === 'danger' ? 'text-red-500' :
                variant === 'warning' ? 'text-amber-500' :
                variant === 'success' ? 'text-green-500' :
                'text-primary'
              }`} />
            </div>
            <div>
              <p className="font-medium text-sm">{title}</p>
              {children}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {badge !== undefined && badge !== null && (
              <Badge variant={variant === 'danger' ? 'destructive' : 'secondary'} className="text-xs">
                {badge}
              </Badge>
            )}
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function QuickActionButton({ icon: Icon, label, onClick, variant = 'default', badge }) {
  const variants = {
    default: 'bg-card border-2 hover:border-primary/50',
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
    danger: 'bg-red-500 text-white hover:bg-red-600',
    success: 'bg-green-500 text-white hover:bg-green-600',
  };

  return (
    <button
      onClick={onClick}
      className={`relative flex flex-col items-center justify-center p-3 rounded-xl transition-all active:scale-95 ${variants[variant]}`}
      style={{ minWidth: MIN_TOUCH_SIZE, minHeight: 72 }}
      data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {badge > 0 && (
        <span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
      <Icon className="h-6 w-6 mb-1" />
      <span className="text-xs font-medium text-center leading-tight">{label}</span>
    </button>
  );
}

function SyncQueueWidget({ stats, onSync, syncing, onViewDetails }) {
  const hasPending = stats.pending > 0 || stats.failed > 0;

  return (
    <Card
      className={`cursor-pointer transition-colors ${hasPending ? 'bg-amber-500/5 border-amber-500/20' : 'bg-card'} hover:bg-muted/50`}
      onClick={onViewDetails}
      data-testid="sync-queue-widget"
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
              hasPending ? 'bg-amber-500/10' : 'bg-green-500/10'
            }`}>
              {syncing ? (
                <RefreshCw className="h-5 w-5 text-primary animate-spin" />
              ) : hasPending ? (
                <Upload className="h-5 w-5 text-amber-500" />
              ) : (
                <CheckCircle className="h-5 w-5 text-green-500" />
              )}
            </div>
            <div>
              <p className="font-medium text-sm">Sync Queue</p>
              <p className="text-xs text-muted-foreground" data-testid="sync-pending-label">
                {syncing ? 'Syncing...' : hasPending ? `${stats.pending + stats.failed} pending` : 'All synced'}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant={hasPending ? 'default' : 'outline'}
            onClick={(e) => { e.stopPropagation(); onSync(); }}
            disabled={syncing || !hasPending}
            data-testid="sync-now-btn"
          >
            {syncing ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Sync Now'}
          </Button>
        </div>
        {(stats.failed > 0 || stats.conflict > 0) && (
          <div className="mt-3 pt-3 border-t flex gap-2">
            {stats.failed > 0 && (
              <Badge variant="destructive" className="text-xs" data-testid="failed-badge">{stats.failed} failed</Badge>
            )}
            {stats.conflict > 0 && (
              <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600">{stats.conflict} conflicts</Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <div className="p-4 space-y-4" data-testid="mobile-dashboard-skeleton">
      <Skeleton className="h-32 w-full rounded-xl" />
      <Skeleton className="h-20 w-full rounded-xl" />
      <div className="grid grid-cols-4 gap-2">
        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-[72px] rounded-xl" />)}
      </div>
      <Skeleton className="h-16 w-full rounded-xl" />
      <Skeleton className="h-16 w-full rounded-xl" />
      <Skeleton className="h-16 w-full rounded-xl" />
    </div>
  );
}

export default function MobileStaffDashboardPage() {
  const navigate = useNavigate();
  const { organisationName } = useBranding();
  const { user } = useAuth();
  const isReviewer = ['Super Admin', 'Admin', 'Coordinator'].includes(user?.role_name);
  const [online, setOnline] = useState(isOnline());
  const [syncing, setSyncing] = useState(false);
  const [queueStats, setQueueStats] = useState({ pending: 0, failed: 0, conflict: 0, synced: 0, total: 0 });
  const { invalidateDashboard, invalidateNotifications } = useCacheInvalidation();

  const { data: dashboardData, isLoading: dashboardLoading, isFetched, refetch: refetchDashboard } = useMobileDashboard();
  const { data: notificationCounts, refetch: refetchNotifications } = useNotificationCounts();

  const loading = dashboardLoading;
  const notificationCount = notificationCounts?.unread || 0;

  const data = {
    nextShift: dashboardData?.nextShift || null,
    nextShiftStart: dashboardData?.nextShiftStart || null,
    checkInStatus: dashboardData?.checkInStatus || 'NO_SHIFT',
    todayShifts: dashboardData?.todayShifts || 0,
    dueMedications: dashboardData?.dueMedications || 0,
    incompleteNotes: dashboardData?.incompleteNotes || 0,
    pendingHandovers: dashboardData?.pendingHandovers || 0,
    handoverRequired: !!dashboardData?.handoverRequired,
    incidentFollowUps: dashboardData?.incidentFollowUps || 0,
    criticalAlerts: dashboardData?.criticalAlerts || 0,
    participantAlerts: dashboardData?.participantAlerts || 0,
    currentShift: dashboardData?.currentShift || null,
  };

  const countdown = useCountdown(data.currentShift ? null : data.nextShiftStart);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    registerDevice();
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const updateStats = async () => {
      const stats = await getQueueStats();
      setQueueStats(stats);
    };
    updateStats();
    const handler = () => updateStats();
    window.addEventListener('offlineQueueUpdated', handler);
    window.addEventListener('syncStatusChanged', handler);
    return () => {
      window.removeEventListener('offlineQueueUpdated', handler);
      window.removeEventListener('syncStatusChanged', handler);
    };
  }, []);

  const fetchData = useCallback(async () => {
    invalidateDashboard();
    invalidateNotifications();
    await Promise.all([refetchDashboard(), refetchNotifications()]);
  }, [invalidateDashboard, invalidateNotifications, refetchDashboard, refetchNotifications]);

  const handleSync = async () => {
    if (!online) {
      toast.error('Cannot sync while offline');
      return;
    }
    setSyncing(true);
    try {
      const results = await syncAllActions();
      if (results.error) {
        toast.error(`Sync failed: ${results.error}`);
      } else if (results.synced > 0) {
        toast.success(`Synced ${results.synced} action(s)`);
        fetchData();
      } else if (results.total === 0) {
        toast.info('Nothing to sync');
      }
    } finally {
      setSyncing(false);
      const stats = await getQueueStats();
      setQueueStats(stats);
    }
  };

  // Check-in now happens on the shift working screen (Day 14 compliance dialog).
  const handleCheckIn = async () => {
    if (data.currentShift) {
      navigate(`/mobile/shifts/${data.currentShift.id}`);
      return;
    }
    if (data.nextShift && data.checkInStatus === 'NOT_CHECKED_IN') {
      navigate(`/mobile/shifts/${data.nextShift.id}`);
      return;
    }
    toast.info('No shift available to check in to right now.');
  };

  const handleCheckOut = async () => {
    if (!data.currentShift) return;
    navigate(`/mobile/shifts/${data.currentShift.id}`);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '-';
    }
  };

  const goTransport = () => {
    const sid = data.currentShift?.id;
    navigate(sid ? `/mobile/transport/new?shift_id=${sid}` : '/mobile/transport/new');
  };

  // Initial loading state (first load, no cached data yet)
  if (loading && !isFetched) {
    return (
      <div className="min-h-screen bg-background pb-24" data-testid="mobile-staff-dashboard">
        <div className="sticky top-0 z-10 bg-background border-b px-4 py-3">
          <h1 className="text-lg font-bold">{organisationName}</h1>
          <p className="text-xs text-muted-foreground">Support Worker</p>
        </div>
        <DashboardSkeleton />
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="mobile-staff-dashboard">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold" data-testid="mobile-dashboard-org-name">{organisationName}</h1>
            <p className="text-xs text-muted-foreground">Support Worker · Today</p>
          </div>
          <div className="flex items-center gap-2">
            <ConnectionStatus online={online} />
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={() => navigate('/mobile/notifications')}
              data-testid="notification-bell"
            >
              <Bell className="h-4 w-4" />
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] font-medium flex items-center justify-center">
                  {notificationCount > 9 ? '9+' : notificationCount}
                </span>
              )}
            </Button>
            <Button variant="ghost" size="icon" onClick={fetchData} disabled={loading} data-testid="dashboard-refresh-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 space-y-4">
        {/* Current / Next Shift Card with countdown + check-in status */}
        {data.currentShift ? (
          <Card className="bg-green-500/5 border-green-500/20" data-testid="current-shift-card">
            <CardContent className="p-4">
              <div
                className="flex items-center gap-3 mb-3 cursor-pointer"
                onClick={() => navigate(`/mobile/shifts/${data.currentShift.id}`)}
                data-testid="current-shift-open"
              >
                <div className="h-12 w-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <Play className="h-6 w-6 text-green-500" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-green-600 font-medium">CURRENT SHIFT</p>
                    <Badge className="bg-green-500/15 text-green-700 text-[10px]" data-testid="checkin-status-badge">Checked in</Badge>
                  </div>
                  <p className="font-bold">{data.currentShift.house_name || data.currentShift.location || data.currentShift.title || 'Active Shift'}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button className="flex-1" variant="outline" onClick={goTransport} data-testid="current-shift-transport-btn">
                  <Truck className="h-4 w-4 mr-2" /> Log Trip
                </Button>
                <Button className="flex-1 bg-red-500 hover:bg-red-600" onClick={handleCheckOut} data-testid="end-shift-btn">
                  <Square className="h-4 w-4 mr-2" /> End Shift
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : data.nextShift ? (
          <Card className="bg-primary/5 border-primary/20" data-testid="next-shift-card">
            <CardContent className="p-4">
              <div
                className="flex items-center gap-3 mb-3 cursor-pointer"
                onClick={() => navigate(`/mobile/shifts/${data.nextShift.id}`)}
                data-testid="next-shift-open"
              >
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-primary font-medium">NEXT SHIFT</p>
                    {countdown && (
                      <Badge variant="secondary" className="text-[10px]" data-testid="next-shift-countdown">{countdown}</Badge>
                    )}
                  </div>
                  <p className="font-bold">{data.nextShift.house_name || data.nextShift.location || data.nextShift.title || 'Shift'}</p>
                  <p className="text-sm text-muted-foreground">
                    {data.nextShift.date ? `${data.nextShift.date} · ` : ''}
                    {data.nextShift.start_time || formatTime(data.nextShift.scheduled_start)}
                  </p>
                </div>
              </div>
              <Button
                className="w-full"
                onClick={handleCheckIn}
                disabled={data.checkInStatus !== 'NOT_CHECKED_IN'}
                data-testid="start-shift-btn"
              >
                <Play className="h-4 w-4 mr-2" />
                {data.checkInStatus === 'NOT_CHECKED_IN' ? 'Check In' : 'Starts Later'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="no-shift-card">
            <CardContent className="p-6 text-center text-muted-foreground">
              <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm font-medium">No upcoming shifts</p>
              <p className="text-xs mt-1">You're all clear. New shifts will appear here.</p>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-2 px-1">QUICK ACTIONS</p>
          <div className="grid grid-cols-4 gap-2" data-testid="mobile-quick-actions">
            <QuickActionButton icon={Play} label="Check In" variant={data.checkInStatus === 'NOT_CHECKED_IN' ? 'primary' : 'default'} onClick={handleCheckIn} />
            <QuickActionButton icon={FileText} label="Note" onClick={() => navigate('/mobile/notes/new')} />
            <QuickActionButton icon={AlertTriangle} label="Incident" variant="danger" onClick={() => navigate('/mobile/incidents/new')} />
            <QuickActionButton icon={ClipboardList} label="Handover" badge={data.handoverRequired ? 1 : 0} onClick={() => navigate('/mobile/handover/new')} />
            <QuickActionButton icon={Pill} label="Medication" badge={data.dueMedications} onClick={() => navigate('/mobile/medications/log')} />
            <QuickActionButton icon={Activity} label="Charts" onClick={() => navigate('/mobile/charts')} />
            <QuickActionButton icon={ShieldAlert} label="Safety" onClick={() => navigate('/mobile/safety')} />
            <QuickActionButton icon={Truck} label="Transport" onClick={goTransport} />
            <QuickActionButton icon={Camera} label="Attachment" onClick={() => navigate('/mobile/attachments')} />
            <QuickActionButton icon={RefreshCw} label="Sync" badge={queueStats.pending + queueStats.failed} onClick={handleSync} />
          </div>
        </div>

        {/* Sync Queue */}
        <SyncQueueWidget
          stats={queueStats}
          onSync={handleSync}
          syncing={syncing}
          onViewDetails={() => navigate('/mobile/sync')}
        />

        {/* Today at a glance */}
        <div className="space-y-3">
          <p className="text-xs font-semibold text-muted-foreground px-1">TODAY AT A GLANCE</p>

          <DashboardCard
            title="Today's Shifts"
            icon={Calendar}
            badge={data.todayShifts || null}
            onClick={() => {
              const target = data.currentShift?.id || data.nextShift?.id;
              if (target) navigate(`/mobile/shifts/${target}`);
            }}
          >
            <p className="text-xs text-muted-foreground">
              {data.todayShifts > 0 ? `${data.todayShifts} shift(s) today` : 'No shifts today'}
            </p>
          </DashboardCard>

          <DashboardCard
            title="Due Medications"
            icon={Pill}
            badge={data.dueMedications || null}
            variant={data.dueMedications > 0 ? 'warning' : 'default'}
            onClick={() => navigate('/mobile/medications/log')}
          >
            <p className="text-xs text-muted-foreground">
              {data.dueMedications > 0 ? 'Action required' : 'All administered'}
            </p>
          </DashboardCard>

          <DashboardCard
            title="Pending Notes"
            icon={FileText}
            badge={data.incompleteNotes || null}
            variant={data.incompleteNotes > 0 ? 'warning' : 'default'}
            onClick={() => navigate('/mobile/notes/new')}
          >
            <p className="text-xs text-muted-foreground">
              {data.incompleteNotes > 0 ? 'Draft notes pending' : 'All complete'}
            </p>
          </DashboardCard>

          <DashboardCard
            title="Handover Required"
            icon={ClipboardList}
            badge={data.handoverRequired ? 'Due' : (data.pendingHandovers || null)}
            variant={data.handoverRequired ? 'warning' : 'default'}
            onClick={() => navigate('/mobile/handover/new')}
          >
            <p className="text-xs text-muted-foreground">
              {data.handoverRequired
                ? 'Write a handover for your current shift'
                : data.pendingHandovers > 0
                  ? `${data.pendingHandovers} received`
                  : 'No handover pending'}
            </p>
          </DashboardCard>

          <DashboardCard
            title="Incident Follow-ups"
            icon={ShieldAlert}
            badge={data.incidentFollowUps || null}
            variant={data.incidentFollowUps > 0 ? 'danger' : 'default'}
            onClick={() => navigate('/mobile/incidents/new')}
          >
            <p className="text-xs text-muted-foreground">
              {data.incidentFollowUps > 0 ? 'Open incidents need review' : 'No open incidents'}
            </p>
          </DashboardCard>

          <DashboardCard
            title="Participant Alerts"
            icon={Bell}
            badge={data.participantAlerts || null}
            variant={data.participantAlerts > 0 ? 'danger' : 'default'}
            onClick={() => navigate('/mobile/notifications')}
          >
            <p className="text-xs text-muted-foreground">
              {data.participantAlerts > 0 ? 'Important alerts for your participants' : 'No participant alerts'}
            </p>
          </DashboardCard>

          {data.criticalAlerts > 0 && (
            <DashboardCard
              title="Critical Alerts"
              icon={AlertTriangle}
              badge={data.criticalAlerts}
              variant="danger"
              onClick={() => navigate('/mobile/notifications')}
            >
              <p className="text-xs text-muted-foreground">Immediate attention</p>
            </DashboardCard>
          )}

          {isReviewer && (
            <DashboardCard
              title="Check-in Exceptions"
              icon={ShieldAlert}
              variant="warning"
              onClick={() => navigate('/mobile/exceptions')}
            >
              <p className="text-xs text-muted-foreground">Review late / manual / geofence check-ins</p>
            </DashboardCard>
          )}
        </div>
      </div>

      <MobileBottomNav />
    </div>
  );
}
